import { AbstractDownloadableService } from "./abstract-downloadable.service";
import {
    Housekeeper, RoomNumber
} from "../models";
import { PaginationCollection } from "../models/pagination-collection.model";
import { mapGetToPostParams } from "../../app/helpers";

/**
 * @class HousekeeperPmsApiService
 * @abstract
 * @extends {AbstractDownloadableService<Item>}
 */
export class HousekeeperPmsApiService extends AbstractDownloadableService {

    /**
     * @type {string}
     * @protected
     */
    get urlPattern() {

        return '/property/:propertyId/housekeeper(/:id)';
    }

    /**
     * @param {Object.<string, *>} attributes
     * @returns {Housekeeper}
     * @protected
     */
    newModel(attributes, ...params) {

        return new Housekeeper(attributes);
    }

    /**
     * @param {Object.<string, *>} attributes
     * @returns {RoomNumber}
     * @protected
     */
    newRoomModel(attributes, ...params) {

        return new RoomNumber(attributes);
    }

    /**
     * @param {Object=} params
     * @param {Property=} property
     * @returns {Promise.<RoomNumber>}
     */
    get(params = {}, property = null) {

        const url = this.url({
            propertyId: property ? property.id : 0
        });

        return this.http
            .get(url, {
                headers: this.authHeaders,
                params,
                ignoreLoadingBar: false
            })
            .then(this.getPaginationCollector())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {Object.<string, *>} paginationData
     * @param {...*} params
     * @returns {PaginationCollection<M>}
     * @protected
     */
    newPaginationCollection(paginationData, ...params) {

        return new PaginationCollection({
            perPage: paginationData.per_page,
            page: paginationData.current_page,
            total: paginationData.total,
            from: paginationData.from,
            to: paginationData.to,
            models: paginationData.data.map(
                (attributes) => this.newRoomModel(attributes, ...params))
        });
    }

    /**
     * @param {Housekeeper} housekeeper
     * @param {Property} property
     * @returns {Promise.<Housekeeper>}
     */
    create(housekeeper, property) {

        const url = this.url({
            propertyId: property.id
        });

        return this.http
            .post(url, housekeeper.toObjectExcept('property', 'housekeeper'), {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {Housekeeper} housekeeper
     * @returns {Promise.<Housekeeper>}
     */
    update(housekeeper) {

        const url = this.url({
            propertyId: housekeeper.property.id,
            id: housekeeper.id
        });

        return this.http
            .put(url, housekeeper.toObjectExcept('property', 'housekeeper'), {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {Housekeeper} housekeeper
     * @returns {Promise.<boolean>}
     */
    delete(housekeeper) {

        const url = this.url({
            propertyId: housekeeper.property_id,
            id: housekeeper.id
        });

        return this.http
            .delete(url, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    getKeepers(property) {

        const url = this.url({
            propertyId: property.id,
        });

        return this.http.get(url + '/get-keepers', {
            headers: this.authHeaders
        })
            .then(this.getMassModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {Property} property
     * @returns {Promise.<Property>}
     */
    dataFilter(property) {

        const url = this.url({
            propertyId: property.id,
        });

        return this.http
            .get(url + '/get-type-rooms', {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {RoomNumber} room
     * @param {Property} property
     * @returns {Promise.<RoomNumber>}
     */
    saveRoom(property, room) {

        const url = this.url({propertyId: property.id}) + '/save-room';

        return this.http
            .post(url ,{rooms:room}, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }


    exportPDF(params = {}, property) {

        const url = this.url({propertyId: property.id}) + '/export-pdf';

        return this.download(url, mapGetToPostParams(params), 'Housekeeping.pdf');
    }

    exportExcel(params = {}, property) {

        const url = this.url({propertyId: property.id}) + '/export-excel';

        return this.download(url, mapGetToPostParams(params), 'Housekeeping.xls');
    }
}



// WEBPACK FOOTER //
// ./src/pms-api/services/housekeeper.service.js